import { useEffect, useMemo, useRef, useState } from "react";
import {
  useCreateOrder,
  useOrderDetail,
  useUpdateOrder,
} from "../../../api/order/index";
import WithAuth from "../../../hoc/WithAuth";
import { useGetAllProducts } from "../../../api/product";
import { useWarehouseList } from "../../../api/admin";
import { AppButton } from "../../../components/ui/AppButton";
import { DeliveryLocation } from "../../../components/ui/DeliveryLocation";
import { Warehouse } from "../../../types/warehouse";
import { StatusModal } from "../../../components/ui/status-modal";
import { Icons } from "../../../assets/images/icons";
import { ModalState } from "../../../types/modal";
import { FormProviderWrapper } from "../../../components/form/FormProviderWrapper";
import { OrderFormData, useOrderForm } from "../../../hooks/useOrderForm";
import { FormSelect } from "../../../components/form/FormSelect";
import { FormInput } from "../../../components/form/FormInput";
import { FormCalendar } from "../../../components/form/FormCalendar";
import { formatDateToEuropean } from "../../../util/date-formatter";
import { useNavigate, useParams } from "react-router-dom";

const CreateOrderPage = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();

  const hasPatched = useRef(false);

  const { mutateAsync: createOrder } = useCreateOrder();
  const { mutateAsync: updateOrder } = useUpdateOrder();

  /** State for data that doesn't belong in the form */
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [productNames, setProductNames] = useState<
    { name: string; id: number; category: string }[]
  >([]);
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    type: "default",
    title: "",
    message: "",
  });

  /** Form management */
  const formMethods = useOrderForm();
  const {
    watch,
    setValue,
    formState: { errors, isValid, isSubmitting },
  } = formMethods;

  /** Watch all form values */
  const productType = watch("productType");
  const product = watch("product");
  const quantity = watch("quantity");
  const locations = watch("locations");
  const tagKey = watch("tagKey");
  const tagValue = watch("tagValue");
  const tags = watch("tags");
  const startDate = watch("startDate");
  const dueDate = watch("dueDate");

  /** Ensure dueDate is never before startDate */
  useEffect(() => {
    if (startDate && dueDate && new Date(dueDate) < new Date(startDate)) {
      setValue("dueDate", startDate);
    }
  }, [startDate, dueDate, setValue]);

  /** API hooks */
  const { data: products } = useGetAllProducts();
  const { data: warehouses } = useWarehouseList();
  const { data: existingOrder } = useOrderDetail(id!);

  /** Normalize incoming warehouses data*/
  const allWarehouses: Warehouse[] = useMemo(
    () =>
      warehouses?.data?.content
        ?.filter((p: any) => p?.id && p?.centerName)
        .map((p: any) => ({
          id: Number(p.id),
          centerName: p.centerName,
          location: p.location || "N/A",
          capacity: Number(p.capacity) || 0,
        })) || [],
    [warehouses],
  );

  /** Collect all selected warehouse IDs */
  const selectedIds = useMemo(
    () => locations.map((l) => l.value?.id).filter(Boolean) as number[],
    [locations],
  );

  /** Check if form can be submitted */
  const canSubmit = useMemo(() => {
    return (
      (isEditMode || isValid) && // ignore isValid in edit mode
      !!product &&
      !!quantity &&
      locations.length > 0 &&
      locations.every((loc) => loc.value) &&
      !isSubmitting
    );
  }, [isValid, product, quantity, locations, isSubmitting, isEditMode]);

  useEffect(() => {
    if (!isEditMode || !existingOrder || hasPatched.current) return;
    if (!products?.data?.productItemDtos?.length || !allWarehouses.length)
      return;

    const order = existingOrder.data.data;

    /** --- Product Type ---  */
    setValue("productType", order.category || "Vegitable");

    /** --- Product ---  */
    const matchingProduct = products.data.productItemDtos.find(
      (p: any) => p.name === order.product,
    );
    setValue("product", matchingProduct?.id?.toString() || "");

    /** --- Quantity ---  */
    setValue("quantity", String(order.quantity || ""), {
      shouldValidate: true,
    });

    /** --- Locations --- */
    const formattedLocations = (order.locations || []).map((loc: any) => {
      const warehouseMatch = allWarehouses.find(
        (w) => w.centerName === loc || w.id === loc.id,
      );

      return {
        /** ID should be unique for each form row */
        id: `row-${Date.now()}-${Math.random()}`,
        value: warehouseMatch || null,
      };
    });

    setValue("locations", formattedLocations, { shouldValidate: true });

    /** --- Tags --- */
    const rawTags = order.tags || "{}";
    const parsedTags =
      typeof rawTags === "string" ? JSON.parse(rawTags) : rawTags;
    const formattedTags = Object.entries(parsedTags).map(([key, value]) => ({
      key,
      value: String(value),
    }));
    setValue("tags", formattedTags);

    /** --- Dates --- */
    const formatDate = (dateStr: string) => {
      if (!dateStr) return "";
      const [year, month, day] = dateStr.includes("/")
        ? dateStr.split("/").reverse()
        : dateStr.split("-");
      return `${year}-${month}-${day}`;
    };
    setValue("startDate", formatDate(order.startingDate));
    setValue("dueDate", formatDate(order.dueDate));

    hasPatched.current = true; // this will mark as done
  }, [
    isEditMode,
    existingOrder,
    setValue,
    allWarehouses.length,
    products?.data.productItemDtos?.length,
    products?.data.productItemDtos,
    allWarehouses,
  ]);

  useEffect(() => {
    /** If locations are empty and warehouses exist, create one empty row */
    if (locations.length === 0 && allWarehouses.length > 0) {
      setValue("locations", [{ id: Date.now().toString(), value: null }]);
    }
  }, [locations, allWarehouses, setValue]);

  /** Effects for product data */
  useEffect(() => {
    if (products?.data?.productItemDtos) {
      const types =
        products.data.productItemDtos.map(
          (p: any) =>
            p.category.charAt(0).toUpperCase() +
            p.category.slice(1).toLowerCase(),
        ) || [];

      setProductTypes(Array.from(new Set(types)));
    }
  }, [products, id]);

  useEffect(() => {
    if (productType && products?.data?.productItemDtos) {
      const _selectedProducts = products.data.productItemDtos.filter(
        (item: any) =>
          item.category.toLowerCase() === productType.toLowerCase(),
      );

      setProductNames(_selectedProducts);
    }
  }, [productType, products]);

  /** Location handlers */
  const handleAddWarehouse = () => {
    const lastRow = locations[locations.length - 1];

    if (locations.length >= allWarehouses.length || !lastRow?.value) return;

    const newLocations = [
      ...locations,
      { id: Date.now()?.toString(), value: null },
    ];
    setValue("locations", newLocations, { shouldValidate: true });
  };

  const handleRemoveWarehouse = (id: string) => {
    const newLocations = locations.filter((item) => item.id !== id);
    setValue("locations", newLocations, { shouldValidate: true });
  };

  const handleSelectWarehouse = (rowId: string, selected: Warehouse | null) => {
    const newLocations = locations.map((item) =>
      item.id === rowId ? { ...item, value: selected } : item,
    );
    setValue("locations", newLocations, { shouldValidate: true });
  };

  /** Tag handlers */
  const handleInsertTag = () => {
    const currentTags = watch("tags") || [];
    const currentKey = watch("tagKey");
    const currentValue = watch("tagValue");

    if (!currentKey || !currentValue) return;

    /** Prevent duplicates */
    const tagExists = currentTags.some((tag) => tag.key === currentKey);
    if (tagExists) {
      console.warn(`Tag with key "${currentKey}" already exists.`);
      return;
    }

    const newTags = [...currentTags, { key: currentKey, value: currentValue }];

    /** Update form values */
    setValue("tags", newTags);
    setValue("tagKey", "");
    setValue("tagValue", "");
  };

  const handleRemoveTag = (key: string) => {
    const newTags = tags.filter((tag) => tag.key !== key);
    setValue("tags", newTags);
  };

  /** Modal handler */
  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const onSubmit = async (data: OrderFormData) => {
    const finalTags = [...(data.tags || [])];
    if (
      data.tagKey &&
      data.tagValue &&
      !finalTags.some((t) => t.key === data.tagKey)
    ) {
      finalTags.push({ key: data.tagKey, value: data.tagValue });
    }

    const _tags = finalTags.reduce(
      (acc, tag) => {
        if (tag.key) acc[tag.key] = tag.value;
        return acc;
      },
      {} as Record<string, string>,
    );

    const order = {
      category: productType,
      locationIds: locations.map((loc) => loc.value?.id),
      quantity: Number(quantity),
      product_id: Number(product),
      starting_date: formatDateToEuropean(data.startDate),
      due_date: formatDateToEuropean(data.dueDate),
      status: "PENDING",
      tags: JSON.stringify(_tags),
    };

    try {
      if (isEditMode) {
        await (updateOrder as any)({ id: id!, ...order });
        setModalState({
          isOpen: true,
          type: "default",
          title: "Order Updated!",
          message: "Your order has been successfully updated.",
          pngIcon: {
            src: Icons.check1Icon,
            alt: "Updated",
            size: 80,
          },
        });
      } else {
        await createOrder(order);
        setModalState({
          isOpen: true,
          type: "default",
          title: "Order Placed!",
          message: "Your order has been placed successfully!",
          pngIcon: {
            src: Icons.check1Icon,
            alt: "Order confirmed",
            size: 80,
          },
        });
      }
      navigate("/my-orders");
    } catch (err) {
      console.error("Error submitting order:", err);
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error",
        message: `Failed to ${isEditMode ? "update" : "create"} the order.`,
      });
    }
  };

  return (
    <FormProviderWrapper methods={formMethods} onSubmit={onSubmit}>
      <div
        className="px-4 md:px-8 py-8 h-screen overflow-y-auto"
        style={{
          fontFamily: "Poppins",
          backgroundColor: "white",
        }}
      >
        {/** Header */}
        <div className="flex justify-between pb-5 items-center">
          <span className="text-2xl font-medium">
            {isEditMode ? "Edit Order" : "What do you want to order?"}
          </span>

          <AppButton
            type="submit"
            className={`${
              canSubmit
                ? "bg-[#08453E] hover:bg-[#0a5850] text-white"
                : "bg-[#DDDDDD] text-gray-500 cursor-not-allowed"
            } transition-colors duration-200`}
          >
            {isSubmitting
              ? "Submitting..."
              : isEditMode
                ? "Update Order"
                : "Submit Order"}
          </AppButton>
        </div>

        <div className="space-y-3">
          {/** Add Product Section */}
          <div className="bg-white border border-[#E6EEF6] rounded-2xl px-6 py-5">
            <div className="flex items-center gap-3 mb-6">
              <h3 className="font-semibold text-black/50">Add Product</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/** Product Type */}
              <div>
                <label className="block text-sm font-medium text-[#09090B] mb-2">
                  Product Type
                </label>
                <FormSelect
                  name="productType"
                  placeholder="Select Product Type"
                  options={productTypes.map((product: string) => ({
                    value: product,
                    label: product,
                  }))}
                />
              </div>

              {/** Product */}
              <div>
                <label className="block text-sm font-medium text-[#09090B] mb-2">
                  Product
                </label>
                <FormSelect
                  name="product"
                  placeholder="Select Product"
                  disabled={!productType}
                  options={productNames.map((p: any) => ({
                    value: p.id?.toString(),
                    label: p.name,
                  }))}
                />
              </div>
            </div>

            {/** Add Quantity Section */}
            <div className="bg-white pt-7">
              <div className="flex items-center gap-3 mb-6">
                <h3 className="font-semibold text-black/50">Add Quantity</h3>
              </div>

              <div className="max-w-[50%] pr-3">
                <label className="block text-sm font-medium text-[#09090B] mb-2">
                  Product Quantity in (Kg)
                </label>
                <FormInput
                  name="quantity"
                  placeholder="Insert Product Quantity"
                />
              </div>
            </div>

            {/** Add delivery location(s) Section */}
            <div className="bg-white mt-7 w-full">
              <div className="flex items-center gap-3 mb-6">
                <h3 className="font-semibold text-black/50">
                  Add Farm Collection Center(s)
                </h3>
              </div>

              {/** Show loading state */}
              {!warehouses && (
                <div className="text-gray-500 text-center py-4">
                  Loading warehouses...
                </div>
              )}

              {/** Show empty state if no warehouses */}
              {warehouses && allWarehouses.length === 0 && (
                <div className="text-gray-500 text-center py-4">
                  No warehouses available
                </div>
              )}

              {/** Locations List */}
              {warehouses && allWarehouses.length > 0 && (
                <>
                  {/** Show when no locations are added yet */}
                  {locations.length === 0 && (
                    <div className="text-gray-500 text-center py-4">
                      Click "Add Farm Collection center" to select delivery
                      locations
                    </div>
                  )}

                  {locations.map((row, index) => {
                    const availableWarehouses = allWarehouses.filter((w) => {
                      /** Show if selected in this row OR not selected elsewhere */
                      if (row.value && w.id === row.value.id) return true;
                      return !selectedIds.includes(w.id);
                    });

                    return (
                      <div
                        key={row.id}
                        className="flex justify-between items-center gap-[10px] mb-3"
                      >
                        <div className="flex-1">
                          <DeliveryLocation
                            value={row.value} // can be null initially
                            warehouses={availableWarehouses}
                            onSelect={(selected) =>
                              handleSelectWarehouse(row.id, selected)
                            }
                            isLocation
                            showHeaders={index === 0}
                          />
                        </div>

                        {index === 0 ? (
                          <AppButton
                            type="button"
                            variant="outline"
                            onClick={handleAddWarehouse}
                            disabled={locations.length >= allWarehouses.length} // only disable if max reached
                            className={`rounded-md border-[#08453E] font-semibold p-4 self-end ${
                              locations.length < allWarehouses.length
                                ? "text-[#08453E]"
                                : "text-gray-400 border-gray-300 cursor-not-allowed"
                            }`}
                          >
                            Add Farm Collection Center
                          </AppButton>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleRemoveWarehouse(row.id)}
                            className="text-red-600 text-sm font-medium hover:underline min-w-36"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {/** Validation error */}
              {errors.locations && (
                <div className="text-red-400 text-sm mt-2">
                  {errors.locations.message}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row w-full gap-3">
            {/** Delivery Dates Section */}
            <div className="bg-white border border-[#E6EEF6] rounded-2xl p-6 w-full">
              <div className="flex items-center gap-3 mb-6">
                <h3 className="font-semibold text-black/50">Delivery Dates</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
                <div>
                  <label className="block text-sm font-medium text-[#09090B] mb-2">
                    Start Date
                  </label>
                  <FormCalendar
                    name="startDate"
                    placeholder="Select Date"
                    disabledDates={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0); // Start of today
                      return date < today; // Disable dates before today
                    }}
                    onValueChange={(val) => {
                      /** Auto-adjust due date if needed*/
                      if (dueDate && new Date(dueDate) < new Date(val)) {
                        setValue("dueDate", val);
                      }
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#09090B] mb-2">
                    Due Date
                  </label>
                  <FormCalendar name="dueDate" placeholder="Select Date" />
                </div>
              </div>
            </div>

            {/** Tags Section */}
            <div className="bg-white border border-[#E6EEF6] rounded-2xl p-6 w-full">
              <div className="flex items-center gap-3 mb-6">
                <h3 className="font-semibold text-black/50">Tags</h3>
              </div>

              {/** Add New Tag Form */}
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pb-3">
                <div className="w-full">
                  <label className="block text-sm font-medium text-[#09090B] mb-2">
                    Key
                  </label>
                  <FormInput name="tagKey" placeholder="Insert Key" />
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium text-[#09090B] mb-2">
                    Value
                  </label>
                  <FormInput name="tagValue" placeholder="Insert Value" />
                </div>

                <AppButton
                  type="button"
                  variant="outline"
                  onClick={handleInsertTag}
                  className={`rounded-md border-[#08453E] font-semibold p-4 self-end ${
                    tagKey && tagValue
                      ? "text-[#08453E]"
                      : "text-gray-400 border-gray-300 cursor-not-allowed"
                  }`}
                  disabled={!tagKey || !tagValue}
                >
                  Add New Tag
                </AppButton>
              </div>

              {/** Existing Tags */}
              {tags.length > 0 && (
                <div className="mt-4">
                  {tags.map((tag) => (
                    <div
                      key={tag.key}
                      className="flex justify-between items-center gap-y-3 pb-3"
                    >
                      <div className="grid grid-cols-2 w-full items-center px-4 py-3 border border-[#E6EEF6] rounded-lg divide-[#E6EEF6]">
                        <span className="font-medium text-[#333]">
                          {tag.key}
                        </span>
                        <span className="text-[#666] pl-4">{tag.value}</span>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag.key)}
                        className="text-red-600 text-sm font-medium hover:underline min-w-36"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <StatusModal
            isOpen={modalState.isOpen}
            onClose={closeModal}
            type={modalState.type}
            title={modalState.title}
            message={modalState.message}
            pngIcon={modalState.pngIcon}
          />
        </div>
      </div>
    </FormProviderWrapper>
  );
};

export default WithAuth(CreateOrderPage);
